import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  input,
  output,
} from '@angular/core'

import { orderBy as _orderBy } from 'lodash/fp'

import { BehaviorSubject, combineLatest } from 'rxjs'

import {
  CustomTemplate,
  GitEvents,
  branchStyle,
  createGitEventLog,
  renderBranchLabel,
  renderDot,
} from '@flowx/designer/shared/components/gitgraph/gitgraph.helpers'
import { Branch, createGitgraph } from '@flowx/gitgraph'

import { ApplicationStatus, ApplicationVersion } from '../../entities/application.entity'

@Component({
  selector: 'flx-gitgraph',
  templateUrl: './gitgraph.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./gitgraph.component.scss'],
})
export class GitgraphComponent implements AfterViewInit, OnDestroy {
  #graphData = new BehaviorSubject<ApplicationVersion[]>([])
  #currentVersionId: BehaviorSubject<string> = new BehaviorSubject<string>(null)
  @ViewChild('graphContainer') graphContainer: ElementRef
  gitGraph

  @Input() set graphData(graphData: ApplicationVersion[]) {
    this.#graphData.next(_orderBy(['originalCreationTimestamp'], ['asc'], graphData))
  }

  get graphData(): ApplicationVersion[] {
    return this.#graphData.value
  }

  @Input() set currentVersionId(currentVersionId: string) {
    this.#currentVersionId.next(currentVersionId)
  }
  get currentVersionId(): string {
    return this.#currentVersionId.value
  }

  graphContainerWidth = output<number>()
  showBranchLabels = input<boolean>(true)
  resizeObserver: ResizeObserver

  ngAfterViewInit(): void {
    combineLatest({
      historyLog: this.#graphData,
      currentVersionId: this.#currentVersionId,
    }).subscribe(({ historyLog }) => {
      this.buildGraph(historyLog)
    })

    this.resizeObserver = new ResizeObserver(() => {
      const bbox = this.graphContainer.nativeElement.getBoundingClientRect()
      this.graphContainerWidth.emit(bbox.width)
    })
    this.resizeObserver.observe(this.graphContainer.nativeElement)
  }

  ngOnDestroy(): void {
    this.resizeObserver.disconnect()
  }

  buildGraph(graphData: ApplicationVersion[]): void {
    if (this.gitGraph) {
      this.graphContainer.nativeElement.children[0]?.remove()
      this.gitGraph.clear()
    }

    this.gitGraph = createGitgraph(this.graphContainer.nativeElement, {
      template: CustomTemplate,
      showBranchLabels: this.showBranchLabels(),
    })

    const gitBranches: Record<string, Branch> = {}
    const gitEvents = createGitEventLog(graphData)
    const currentBranchId = graphData.find(
      (version) => version.id === this.currentVersionId
    )?.branchId

    gitEvents.forEach((event) => {
      const isCurrentVersion = event.log.id === this.currentVersionId
      const highlighted = !currentBranchId || event.log.branchId === currentBranchId

      switch (event.eventType) {
        case GitEvents.BRANCH: {
          const branchOptions: any = {
            name: event.log.branchName,
            renderLabel: renderBranchLabel,
            style: branchStyle(event.log, highlighted),
          }

          // If we have a parent branch name, branch from it
          if (event.parentBranchName && gitBranches[event.parentBranchName]) {
            branchOptions.from = gitBranches[event.parentBranchName]
          }

          return (gitBranches[event.log.branchName] = this.gitGraph.branch(branchOptions))
        }
        case GitEvents.COMMIT: {
          return gitBranches[event.log.branchName].commit({
            subject: event.log.commitMessage,
            renderDot: renderDot.bind(null, event.log, isCurrentVersion, highlighted),
            onClick: () => {},
          })
        }
        case GitEvents.MERGE: {
          const mergedFromBranch = this.graphData.find(
            (_branch) => _branch.id === event.log.mergedFromVersionId
          )?.branchName

          // The merged-from version can be absent from the history (e.g. its branch was
          // deleted) — render a plain commit instead of a merge edge to an unknown branch
          if (!mergedFromBranch || !gitBranches[mergedFromBranch]) {
            return gitBranches[event.log.branchName].commit({
              subject: event.log.commitMessage,
              renderDot: renderDot.bind(null, event.log, isCurrentVersion, highlighted),
              onClick: () => {},
            })
          }

          return gitBranches[event.log.branchName].merge({
            branch: mergedFromBranch,
            commitOptions: {
              subject: event.log.commitMessage,
              renderDot: renderDot.bind(null, event.log, isCurrentVersion, highlighted),
              dashedEdge: event.log.status === ApplicationStatus.MERGE_IN_PROGRESS,
              onClick: () => {},
            },
          })
        }
      }
    })

    // ResizeObserver callbacks only run while the tab renders frames, so consumers relying on
    // graphContainerWidth (e.g. horizontal scroll sizing) would stay unsized until the next
    // repaint — emit the width after every rebuild as well. Deferred one tick because the lib
    // sizes the SVG asynchronously (microtask) after the events are processed.
    setTimeout(() => {
      this.graphContainerWidth.emit(this.graphContainer.nativeElement.getBoundingClientRect().width)
    })
  }
}
